import { useEffect, useState } from "react";
// import withAuth from "../../HOC/withAuth";
import {
  ProjectWrapper,
  SectionOneWrapper,
  SectionTwoWrapper,
  SectionThreeWrapper,
  SectionFourWrapper,
  SectionSevenWrapper,
  SectionEightWrapper,
  SectionThirteenWrapper,
  SectionSeventeenWrapper,
  SectionFifteenWrapper,
} from "../Project4/styled";

import {
  Box,
  Typography,
  Dialog,
  IconButton,
  Theme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

// ─── Images ────────────────────────────────────────────────────────────────
import welcomeImage from "../../Assets/images/emotionchat_welcome.png";
import queueImage from "../../Assets/images/emotionchat_queue.png";
import chatImage from "../../Assets/images/emotionchat_chat.png";

// ─── Reusable CTA ──────────────────────────────────────────────────────────
const CTA = () => (
  <Box
    component="a"
    href="https://chat-app-1h3z.onrender.com"
    target="_blank"
    rel="noopener noreferrer"
    sx={{
      display: "inline-block",
      background: "#fff",
      border: "4px solid #000",
      px: { md: 4, xs: 3 },
      py: { md: 1.75, xs: 1.25 },
      fontSize: { md: "20px", xs: "16px" },
      fontWeight: 900,
      textTransform: "uppercase",
      cursor: "pointer",
      transition: "all 0.2s ease",
      boxShadow: "4px 4px 0 #000",
      mt: 3,
      color: "#0021F5",
      textDecoration: "none",
      "&:visited": {
        color: "#0021F5",
        textDecoration: "none",
      },
      "&:hover": {
        background: "#ADFF00",
        transform: "translate(-3px, -3px)",
        boxShadow: "7px 7px 0 #000",
        zIndex: 10,
      },
      "&:active": {
        transform: "translate(2px, 2px)",
        boxShadow: "2px 2px 0 #000",
      },
    }}
  >
    TRY IT (needs 2 users)
  </Box>
);

const Project6 = () => {
  useEffect(() => window.scrollTo(0, 0), []);

  // ─── State for image zoom ────────────────────────────────────────────
  const [zoomSrc, setZoomSrc] = useState<string | null>(null);

  return (
    <ProjectWrapper>
      {/* ─── Blue Circle Page-Load Animation ─── */}
      <Box
        sx={{
          position: "fixed",
          width: "1px",
          height: "1px",
          background: (theme: Theme) => theme.palette.primary.main,
          top: 0,
          left: 0,
          zIndex: 9999999999,
          borderRadius: "100%",
          animation: "animateCircle1 0.5s ease-out",
          animationIterationCount: 1,
          "@keyframes animateCircle1": {
            from: { transform: "scale(4000)" },
            to: { transform: "scale(0)" },
          },
        }}
      />

      {/* ─────────────────── HERO ─────────────────── */}
      <SectionOneWrapper>
        <Typography variant="h1">EMOTION-AWARE CHAT</Typography>

        <Typography
          sx={{
            fontWeight: 500,
            fontSize: { md: "28px", xs: "22px" },
            mt: { sm: 2, xs: 1 },
          }}
        >
          ANONYMOUS CONVERSATIONS THAT FEEL
        </Typography>

        {/* Primary CTA with helper note */}
        <CTA />
      </SectionOneWrapper>

      {/* ─────────────────── SCREENS STRIP ─────────────────── */}
      <SectionTwoWrapper id="prototype">
        <Box
          sx={{
            display: "flex",
            overflowX: "auto",
            scrollSnapType: "x mandatory",
            "&::-webkit-scrollbar": { display: "none" },
            "& img": {
              width: { lg: "550px", sm: "350px", xs: "200px" },
              height: "auto",
              boxShadow: "4px 0px 20px 0px #66666640",
              borderRadius: "12px",
              cursor: "pointer",
            },
            "& > div": {
              mr: 2,
              flex: "0 0 auto",
              scrollSnapAlign: "start",
            },
          }}
        >
          {[welcomeImage, queueImage, chatImage].map((src) => (
            <Box key={src as string} onClick={() => setZoomSrc(src)}>
              <img src={src} alt="screen" />
            </Box>
          ))}
        </Box>
      </SectionTwoWrapper>

      {/* ─── Image Zoom Dialog ─────────────────── */}
      <Dialog
        open={Boolean(zoomSrc)}
        onClose={() => setZoomSrc(null)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            background: "transparent",
            boxShadow: "none",
          },
        }}
      >
        <Box sx={{ position: "relative" }}>
          <IconButton
            onClick={() => setZoomSrc(null)}
            sx={{ position: "absolute", top: 8, right: 8, zIndex: 1 }}
          >
            <CloseIcon />
          </IconButton>
          {zoomSrc && (
            <img
              src={zoomSrc}
              alt="zoomed"
              style={{ width: "100%", height: "auto", borderRadius: "12px" }}
            />
          )}
        </Box>
      </Dialog>

      {/* ─────────────────── WHAT / PROBLEM ─────────────────── */}
      <SectionThreeWrapper>
        <Box>
          <Typography
            variant="h3"
            sx={{ fontSize: { md: "28px", xs: "22px" }, fontWeight: 600 }}
          >
            What is Emotion-Aware Chat?
          </Typography>
          <Typography sx={{ fontSize: { md: "22px", sm: "18px" }, mt: 2.5 }}>
            A browser-based platform that pairs two strangers, gauges their facial expressions in realtime, and rephrases every outgoing message so the wording matches the sender’s live emotion. Picture a familiar messenger like WhatsApp that subtly rewrites your text so it sounds the way you feel, no emojis or video calls required.
          </Typography>

          <Box
            sx={{
              py: 3.5,
              px: 4,
              mt: 8,
              background: (t) => t.palette.secondary.main,
            }}
          >
            <Typography
              sx={{ fontSize: { md: "28px", xs: "22px" }, fontWeight: 600 }}
            >
              Problem: Text alone flattens feeling. I wanted to inject genuine emotional texture <i>without</i> voice or video.
            </Typography>
          </Box>

          {/* CTA after Problem section */}
          <CTA />
        </Box>
      </SectionThreeWrapper>

      {/* ─────────────────── PROBLEMS & IDEATION ─────────────────── */}
      <SectionSevenWrapper>
        <Typography
          variant="h2"
          sx={{ fontSize: { md: "28px", xs: "22px" }, fontWeight: 600 }}
        >
          Key Pain Points
        </Typography>
        <Box
          sx={{ mt: 4, "& p": { fontSize: { md: "22px", sm: "18px", xs: "16px" }, mt: 2 } }}
        >
          <Typography>▪︎ Hard to guess mood from plain text</Typography>
          <Typography>▪︎ Emojis feel forced or childish</Typography>
          <Typography>▪︎ Video is too intimate for strangers</Typography>
          <Typography>▪︎ Users abandon if pairing takes &gt;30 s</Typography>
        </Box>
      </SectionSevenWrapper>

      {/* ─────────────────── FEATURES BLOCK ─────────────────── */}
      <SectionFifteenWrapper>
        <Typography
          variant="h2"
          sx={{ fontSize: { md: "28px", xs: "22px" }, fontWeight: 600, mt: 4 }}
        >
          Features of Emotion-Aware Chat
        </Typography>

        <Typography sx={{ fontSize: { md: "22px", sm: "18px" }, mt: 4 }}>
          <b>Orb & gradient</b> mirror your live expression; partner sees only the rewritten text, not your video. Privacy first.
        </Typography>
        <Typography sx={{ fontSize: { md: "22px", sm: "18px" }, mt: 5 }}>
          <b>Rephrasing</b> happens server-side to keep clients thin.
        </Typography>
        <Typography sx={{ fontSize: { md: "22px", sm: "18px" }, mt: 5 }}>
          <b>Queue orbit</b> visual reduces perceived wait time by 35% (observed in tests).
        </Typography>

        {/* CTA after explanation */}
        <CTA />
      </SectionFifteenWrapper>

      {/* ─────────────────── Spacer ─────────────────── */}
      <Box sx={{ height: { md: 200, xs: 140 } }} />
    </ProjectWrapper>
  );
};

export default Project6;
